/* Keeping the fixed bottom chrome inside the device.

   `position: fixed; bottom: 0` anchors to the *layout* viewport. On a phone the
   layout viewport is regularly taller than the part of the screen you can
   actually see, and every one of those cases puts the tab bar's labels
   underneath something:

   1. The collapsing address bar. Handled by sizing every page shell in `svh`
      rather than `dvh` — a dynamic unit inside a *minimum* height reflows the
      document on every frame of the toolbar animation, and a bar anchored to
      a document that keeps resizing is a bar that visibly drifts.

   2. The on-screen keyboard, on browsers that honour
      `interactive-widget=resizes-content` — the layout viewport shrinks, so
      `bottom: 0` is already above the keyboard. Handled in index.html.

   3. The on-screen keyboard on browsers that ignore that hint, which today
      means iOS. There the keyboard shrinks only the *visual* viewport while
      the layout viewport keeps its full height, so `bottom: 0` sits behind
      the keyboard and the bar appears to slide as the page is scrolled under
      it.

   4. The system navigation bar on Android. `viewport-fit=cover` lets the page
      paint under the gesture handle, and `env(safe-area-inset-bottom)` is
      supposed to say how much room to leave — but Chrome extends the layout
      viewport under the handle while still reporting `0px` outside of
      edge-to-edge mode. The bar is then laid out correctly against a number
      that is wrong, and the system pill sits on top of the labels.

   3 and 4 are the same measurement — how much of the layout viewport's bottom
   is not visible — and this file publishes both:

     `--kb`    a keyboard, which bars lift *above*
     `--occl`  persistent chrome, which bars pad *around*

   They are separated because the two want opposite treatment. A bar lifted
   above a keyboard must not also reserve room for a home indicator it is
   nowhere near; a bar resting on the floor must reserve room for the system
   navigation it is sitting behind. `--occl` feeds `--safe-b` through a `max()`
   in tokens.css rather than a sum, because on a device that reports a real
   inset the inset and the occlusion are two measurements of one obstacle.

   ── Why the split is by size ────────────────────────────────────────────────

   A keyboard takes a third of the screen. System chrome takes a few dozen
   pixels. Nothing sits in between, so the size of the overlap identifies which
   one it is, and the threshold can be generous enough that the classification
   is never in doubt.

   ── Why both are written every frame ────────────────────────────────────────

   A keyboard animates in over ~250ms and the bar has to ride up with it.

   The occlusion is the less obvious case. It changes on every frame of a
   toolbar animation, and the instinct is to wait for it to settle — but
   waiting is what makes the bar visibly move. A bar at `bottom: 0` padded by
   `--occl` has its content edge at

       innerHeight − occlusion − inset  ≡  visualViewport.height − inset

   which depends only on the visible height, and the visible height does not
   change while the toolbar animates. Tracked live, the bar is *exactly* still
   through the whole animation. Delayed by even a few frames, it slides down
   with `innerHeight` and snaps back when the value lands — which is the drift
   this file exists to remove, reintroduced by the mechanism meant to prevent
   it.

   What must not be live is anything in the document flow: `--occl-max` is a
   high-water mark for those, so a page's bottom reservation is not
   recalculated sixty times a second for a few pixels nobody can see. */

import { useEffect } from 'react'

/** Below this, an overlap is chrome. At or above it, it is a keyboard. */
export const KEYBOARD_MIN = 80

/** Under a few pixels is sub-pixel rounding between two viewport measures. */
export const OCCLUSION_MIN = 4

/** One reading of the two viewports, and the page state that qualifies it. */
export interface ViewportSample {
  /** Layout viewport height — what `bottom: 0` is measured against. */
  innerHeight: number
  /** Visible height. */
  visualHeight: number
  /** How far the visual viewport has been scrolled down inside the layout one. */
  offsetTop: number
  /** Pinch-zoom factor. */
  scale: number
  /** Height of a classic horizontal scrollbar, which `innerHeight` counts and
   *  `visualViewport.height` does not. Zero on every phone. */
  scrollbarHeight: number
}

export interface BottomInsets {
  /** Overlap caused by an on-screen keyboard. Bars sit above it. */
  keyboard: number
  /** Overlap caused by browser or system chrome. Bars pad around it. */
  occlusion: number
}

const NONE: BottomInsets = { keyboard: 0, occlusion: 0 }

/** Splits the gap between the two viewports into the two things that cause it.
 *
 *  Returns `null` when the sample cannot be trusted — while the page is
 *  pinch-zoomed the visual viewport is a window onto the page rather than a
 *  description of the screen, and its height says nothing about what is
 *  covering the bottom of it. The caller holds its last good values instead of
 *  reacting to a number that does not mean what it usually means. */
export function classifyBottomOverlap(s: ViewportSample): BottomInsets | null {
  if (!Number.isFinite(s.innerHeight) || !Number.isFinite(s.visualHeight)) return null
  // A tolerance rather than `> 1`: browsers report 1.0000000000000002 at rest.
  if (s.scale > 1.01) return null

  const overlap = s.innerHeight - s.scrollbarHeight - s.visualHeight - s.offsetTop
  if (!(overlap > 0)) return NONE

  const px = Math.round(overlap)
  if (px >= KEYBOARD_MIN) return { keyboard: px, occlusion: 0 }
  if (px >= OCCLUSION_MIN) return { keyboard: 0, occlusion: px }
  return NONE
}

function read(vv: VisualViewport): ViewportSample {
  /* `documentElement.clientHeight` is the layout viewport minus a horizontal
     scrollbar; `window.innerHeight` includes it. Subtracting the difference is
     what stops a desktop page that happens to overflow sideways from reading
     its own scrollbar as a system navigation bar and padding every bottom bar
     by 15px. Phones use overlay scrollbars, so this is 0 where it matters. */
  const scrollbarHeight = Math.max(0, window.innerHeight - document.documentElement.clientHeight)

  return {
    innerHeight: window.innerHeight,
    visualHeight: vv.height,
    offsetTop: vv.offsetTop,
    scale: vv.scale,
    scrollbarHeight,
  }
}

export function useKeyboardInset(): void {
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const root = document.documentElement
    let raf = 0
    let lastKb = -1
    let lastOccl = -1
    let maxOccl = 0

    const apply = () => {
      const insets = classifyBottomOverlap(read(vv))
      // Unmeasurable — mid-pinch. Hold what we had rather than react to a
      // height that describes the magnifier instead of the screen.
      if (!insets) return

      if (insets.keyboard !== lastKb) {
        lastKb = insets.keyboard
        root.style.setProperty('--kb', `${insets.keyboard}px`)
      }

      if (insets.occlusion !== lastOccl) {
        lastOccl = insets.occlusion
        root.style.setProperty('--occl', `${insets.occlusion}px`)
      }

      /* Monotonic on purpose. This one is read by reservations in the document
         flow, and letting it fall as the toolbar re-expands would re-lay-out
         every page to reclaim a strip of padding below the fold. */
      if (insets.occlusion > maxOccl) {
        maxOccl = insets.occlusion
        root.style.setProperty('--occl-max', `${maxOccl}px`)
      }
    }

    const sync = () => {
      cancelAnimationFrame(raf)
      // visualViewport fires a burst of events per transition; one write per
      // frame is all the style system can use anyway.
      raf = requestAnimationFrame(apply)
    }

    apply()

    vv.addEventListener('resize', sync)
    vv.addEventListener('scroll', sync)

    /* An orientation change swaps which edge the system bars are on, so the
       high-water mark learned in the other orientation no longer describes
       anything. Some Android builds also fire no visualViewport event for it
       at all, which is the other half of why this listener exists. */
    const onOrientation = () => {
      maxOccl = 0
      root.style.setProperty('--occl-max', '0px')
      sync()
    }
    window.addEventListener('orientationchange', onOrientation)

    return () => {
      cancelAnimationFrame(raf)
      vv.removeEventListener('resize', sync)
      vv.removeEventListener('scroll', sync)
      window.removeEventListener('orientationchange', onOrientation)
      // Back to the tokens' declared 0px rather than to nothing.
      root.style.removeProperty('--kb')
      root.style.removeProperty('--occl')
      root.style.removeProperty('--occl-max')
    }
  }, [])
}
